import "../../../design/findTeacher.css";
import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { API_URL, PUBLIC_URL } from "../../../api/API_SERVERS";
import { FaLocationDot, FaPhone, FaChevronDown, FaXmark, FaMagnifyingGlass, FaRoute } from "react-icons/fa6";
import Loading from "../../../utils/components/Loading";
import Toast from "../../../utils/components/Toast";

// მარკერების კონფიგურაცია
const redIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    shadowSize: [41, 41]
});

const blueIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    shadowSize: [41, 41]
});

const subjectsData = [
    { value: "აიტი", label: "აიტი" }, { value: "ბიოლოგია", label: "ბიოლოგია" },
    { value: "ბუნება", label: "ბუნება" }, { value: "გეოგრაფია", label: "გეოგრაფია" },
    { value: "გერმანული", label: "გერმანული" }, { value: "ინგლისური", label: "ინგლისური" },
    { value: "იტალიური", label: "იტალიური" }, { value: "ისტორია", label: "ისტორია" },
    { value: "მათემატიკა", label: "მათემატიკა" }, { value: "მუსიკა", label: "მუსიკა" },
    { value: "პროგრამირება", label: "პროგრამირება" }, { value: "რუსული", label: "რუსული" },
    { value: "სამოქალაქო", label: "სამოქალაქო" }, { value: "ფრანგული", label: "ფრანგული" },
    { value: "ფიზიკა", label: "ფიზიკა" }, { value: "ქართული", label: "ქართული" },
    { value: "ქიმია", label: "ქიმია" }, { value: "ჭადრაკი", label: "ჭადრაკი" },
    { value: "ხელოვნება", label: "ხელოვნება" },
].sort((a, b) => a.label.localeCompare(b.label, 'ka'));

const levelsData = ["დაწყებითები", "საშუალო", "უმაღლესი", "დაწყებითები+საშუალო", "დაწყებითები+უმაღლესი", "საშუალო+უმაღლესი", "ყველა"];
const experienceData = [
    { label: "0-1 წელი", value: "0-1" },
    { label: "2-5 წელი", value: "2-5" },
    { label: "6-10 წელი", value: "6-10" },
    { label: "10+ წელი", value: "10+" }
];

// რუკის დამხმარე კომპონენტები
function LocationSelector({ setUserLocation }) {
    useMapEvents({
        click(e) { setUserLocation([e.latlng.lat, e.latlng.lng]); }
    });
    return null;
}

function MapResizer({ viewMode }) {
    const map = useMap();
    useEffect(() => {
        const timer = setTimeout(() => { map.invalidateSize(); }, 300);
        return () => clearTimeout(timer);
    }, [viewMode, map]);
    return null;
}

function FlyToTeacher({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position && Array.isArray(position) && !isNaN(position[0])) {
            map.flyTo(position, 16, { duration: 1.5 });
        }
    }, [position, map]);
    return null;
}

const calculateDistance = (loc1, loc2) => {
    if (!loc1 || !loc2 || isNaN(loc1[0])) return 0;
    const R = 6371;
    const dLat = (loc2[0] - loc1[0]) * Math.PI / 180;
    const dLon = (loc2[1] - loc1[1]) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(loc1[0] * Math.PI / 180) * Math.cos(loc2[0] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export default function FindTeacher() {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [subject, setSubject] = useState("");
    const [subjectSearch, setSubjectSearch] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [viewMode, setViewMode] = useState("list");
    const dropdownRef = useRef(null);
    const [locationFilter, setLocationFilter] = useState("");

    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [level, setLevel] = useState("");
    const [experience, setExperience] = useState("");

    const [radius, setRadius] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [userLocation, setUserLocation] = useState(null);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [totalTeachers, setTotalTeachers] = useState(0);
    const [toastConfig, setToastConfig] = useState(null);

    const fetchTeachers = useCallback(async (targetPage = 1) => {
        try {
            setLoading(true);
            const params = {
                page: targetPage,
                limit: 10,
                subject: subject || undefined,
                minPrice: minPrice || undefined,
                maxPrice: maxPrice || undefined,
                level: level || undefined,
                experience: experience || undefined,
                lessonLocation: locationFilter || undefined
            };
            if (userLocation && radius) {
                params.userLat = userLocation[0];
                params.userLng = userLocation[1];
                params.radius = radius;
            }

            const res = await axios.get(`${API_URL}${PUBLIC_URL}`, { params });
            setTeachers(res.data.activeTeachers || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalTeachers(res.data.total || 0);
            setPage(targetPage);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [subject, minPrice, maxPrice, level, experience, userLocation, radius, locationFilter]);

    useEffect(() => {
        fetchTeachers(1);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getLevelClass = (lvl) => {
        if (!lvl) return "";
        return `lvl-${lvl.toLowerCase().replace(/\s+/g, '').replace(/\+/g, '-plus-')}`;
    };

    const getExpClass = (exp) => {
        const years = exp?.toString();
        if (!years) return "";
        const mapping = { "0-1": "exp-0-1", "2-5": "exp-2-5", "6-10": "exp-6-10", "10+": "exp-10-plus" };
        return mapping[years] || "";
    };

    const openRoute = (e, location) => {
        e.stopPropagation();
        if (location && Array.isArray(location)) {
            const url = `https://www.google.com/maps/dir/?api=1&destination=${location[0]},${location[1]}&travelmode=transit`;
            window.open(url, "_blank");
        }
    };

    const filteredSubjects = subjectsData.filter(s => s.label.toLowerCase().includes(subjectSearch.toLowerCase()));

    if (loading) return <Loading />;

    return (
        <div id="findTeacher" className="findTeacherSection">
            {toastConfig && <Toast {...toastConfig} onClose={() => setToastConfig(null)} />}

            <div className={`teacherMainLayout ${viewMode}`}>
                <div className="leftPanel">
                    <div className="filterPanel">
                        <div className="filterGrid">
                            <div className="searchableDropdown" ref={dropdownRef}>
                                <div className="dropdownInputWrapper" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                    <input
                                        type="text"
                                        placeholder="აირჩიეთ საგანი"
                                        readOnly={!isDropdownOpen}
                                        value={isDropdownOpen ? subjectSearch : (subject || "")}
                                        onChange={(e) => {
                                            setSubjectSearch(e.target.value);
                                            setIsDropdownOpen(true);
                                        }}
                                    />
                                    {subject ? <FaXmark className="clearIcon" onClick={(e) => {
                                        e.stopPropagation();
                                        setSubject("");
                                        setSubjectSearch("");
                                    }} /> : <FaChevronDown className="arrowIcon" />}
                                </div>
                                <AnimatePresence>
                                    {isDropdownOpen && (
                                        <motion.ul className='dropdownList' initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
                                            <li onClick={() => { setSubject(""); setIsDropdownOpen(false); setSubjectSearch(""); }}>ყველა საგანი</li>
                                            {filteredSubjects.map(sub => (
                                                <li key={sub.value} onClick={() => {
                                                    setSubject(sub.value);
                                                    setSubjectSearch("");
                                                    setIsDropdownOpen(false);
                                                }}>{sub.label}</li>
                                            ))}
                                        </motion.ul>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="filterDropdown">
                                <select value={level} onChange={(e) => setLevel(e.target.value)}>
                                    <option value="">სწავლის დონე</option>
                                    {levelsData.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>

                            <div className="filterDropdown">
                                <select value={experience} onChange={(e) => setExperience(e.target.value)}>
                                    <option value="">გამოცდილება</option>
                                    {experienceData.map(exp => <option key={exp.value} value={exp.value}>{exp.label}</option>)}
                                </select>
                            </div>

                            <div className="priceRangeGroup">
                                <input placeholder="მინ ₾" type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
                                <input placeholder="მაქს ₾" type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
                            </div>

                            <div className="filterDropdown">
                                <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                                    <option value="">სად ჩატარდება?</option>
                                    <option value="to_student">🏠 მოწაფესთან</option>
                                    <option value="to_teacher">🏫 მასწავლებელთან</option>
                                </select>
                            </div>

                            <div className="radiusWrapper">
                                <select value={radius || ""} onChange={e => setRadius(Number(e.target.value))} disabled={!userLocation}>
                                    <option value="" disabled>{userLocation ? "რადიუსი" : "მონიშნეთ რუკაზე"}</option>
                                    {[0.1, 0.5, 1, 2, 5, 10, 20].map(r => (
                                        <option key={r} value={r}>{r < 1 ? `${r * 1000} მ` : `${r} კმ`}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <button className="searchBtn" onClick={() => fetchTeachers(1)}>
                            <FaMagnifyingGlass /> <div>ძებნა</div>
                        </button>
                    </div>

                    <div className="rightPanel">
                        <MapContainer center={[41.7151, 44.8271]} zoom={12} className="teacherMap">
                            <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
                            <LocationSelector setUserLocation={setUserLocation} />
                            <FlyToTeacher position={selectedTeacher} />
                            <MapResizer viewMode={viewMode} />
                            {userLocation && <Marker position={userLocation} icon={blueIcon} />}
                            {userLocation && radius && <Circle center={userLocation} radius={radius * 1000} fillOpacity={0.1} color="#2563eb" />}
                            {teachers.map((t) => t.location && <Marker key={t.person_id} position={t.location} icon={redIcon} />)}
                        </MapContainer>
                    </div>
                </div>

                <div className={'middlePanel'}>
                    <div className="panelHeader">
                        <p className="resultsCount">ნაპოვნია <b>{totalTeachers}</b> მასწავლებელი</p>
                        <div className="pagination">
                            <button disabled={page === 1} onClick={() => fetchTeachers(page - 1)}>უკან</button>
                            <span>{page} / {totalPages}</span>
                            <button disabled={page === totalPages} onClick={() => fetchTeachers(page + 1)}>წინ</button>
                        </div>
                    </div>

                    <div className="teacherList">
                        {teachers.length === 0 && !loading && <div className="no-results">მასწავლებლები ვერ მოიძებნა 🧐</div>}
                        {teachers.map((teacher) => (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                key={teacher.person_id}
                                className="subject-card"
                                onClick={() => {
                                    if (teacher.location) setSelectedTeacher(teacher.location);
                                    if (window.innerWidth < 1024) setViewMode("map");
                                }}
                            >
                                <div className="card-header">
                                    <div className="header-info">
                                        <h4 className="teacher-name">{teacher.fullname}</h4>
                                        <span className="address-text"><FaLocationDot /> {teacher.address}</span>
                                    </div>
                                    <div className="header-actions">
                                        <a href={`tel:${teacher.mobile_number}`} className="phone-btn" onClick={e => e.stopPropagation()}><FaPhone /> {teacher.mobile_number}</a>
                                        {teacher.location && (
                                            <button className="route-btn" onClick={(e) => openRoute(e, teacher.location)}>
                                                <FaRoute /> მარშრუტი
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="subjects-container">
                                    {teacher.teacher_subjects?.map((sub, idx) => (
                                        <div key={idx} className={`mini-subject-row ${sub.lesson_location ? 'loc-student' : 'loc-teacher'}`}>
                                            <div className="sub-top">
                                                <span className="sub-title">{sub.name}
                                                    <small style={{ fontSize: '9px', display: 'block', opacity: 0.8 }}>
                                                        {sub.lesson_location ? "🏠 მოწაფესთან" : "🏫 მასწავლებელთან"}
                                                    </small>
                                                </span>
                                                <span className="sub-price">{sub.price}₾<span>/სთ</span></span>
                                            </div>
                                            <div className="sub-badges">
                                                <span className={`sub-find-badge ${getLevelClass(sub.level)}`}>{sub.level}</span>
                                                <span className={`sub-find-badge ${getExpClass(sub.experience)}`}>{sub.experience} წელი</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {userLocation && teacher.location && (
                                    <div className="dist-tag">📏 {calculateDistance(userLocation, teacher.location).toFixed(1)} კმ თქვენგან</div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}